import React, { useEffect, useState } from 'react';
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import CurrencyInput from 'react-currency-input-field';
import cogoToast from 'cogo-toast';
import { copInputProps, valorFormat } from '../../helpers/formatters';
import { recaudoServices } from '../../services';

const pesos = v => `$ ${valorFormat(Number(v))}`;

// Corrige el valor de un pago que llegó en un recaudo por lote, sin buscar el lote ni la línea:
// usa la misma corrección de lote (reemplaza la línea y ajusta tesorería por la diferencia).
function CorregirPagoModal({ pago, toggle, onSaved }) {
  const [valor, setValor] = useState(0);
  const [motivo, setMotivo] = useState('');
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (!pago) return;
    setValor(pago.valorRecaudo);
    setMotivo('');
  }, [pago]);

  if (!pago) return null;

  const diferencia = valor - pago.valorRecaudo;

  const onGuardar = async () => {
    if (!valor || valor <= 0) {
      cogoToast.warn('El valor debe ser mayor que cero. Para quitar el pago use Anular.',
        { position: 'bottom-left', heading: 'Valor inválido' });
      return;
    }
    if (diferencia === 0) {
      cogoToast.warn('El valor es igual al registrado', { position: 'bottom-left', heading: 'Sin cambios' });
      return;
    }
    if (!motivo.trim()) {
      cogoToast.warn('Indique el motivo de la corrección', { position: 'bottom-left', heading: 'Campos requeridos' });
      return;
    }
    setRequesting(true);
    const response = await recaudoServices.corregirLote({
      lote: pago.lote,
      observacion: motivo.trim(),
      recaudos: [{ recaudo: pago.idRecaudo, valorRecaudo: valor }],
    });
    setRequesting(false);
    if (!response || response.error) {
      cogoToast.error(response?.msg || 'No se logró corregir el pago', { position: 'bottom-left', heading: 'Error' });
      return;
    }
    cogoToast.success(`Pago corregido. Se registró en tesorería un ${diferencia > 0 ? 'ingreso' : 'egreso'} por ${pesos(Math.abs(diferencia))}.`,
      { position: 'bottom-left', heading: 'Corregido' });
    onSaved();
  };

  return (
    <Modal isOpen={!!pago} toggle={toggle}>
      <ModalHeader toggle={toggle}>Corregir valor del pago</ModalHeader>
      <ModalBody>
        <p className="fs--1 mb-3">
          {`Período ${pago.periodo} · corte ${pago.fechaCorte || ''} · pagado ${pago.fecha || ''} · lote ${pago.lote}`}
        </p>
        <FormGroup>
          <Label>Valor registrado</Label>
          <div className="font-weight-semi-bold">{pesos(pago.valorRecaudo)}</div>
        </FormGroup>
        <FormGroup>
          <Label for="valorCorregido">Valor correcto</Label>
          <CurrencyInput
            id="valorCorregido"
            className="form-control"
            allowNegativeValue={false}
            prefix="$"
            {...copInputProps}
            defaultValue={pago.valorRecaudo}
            onValueChange={v => setValor(v ? Number(v.replaceAll(',', '.')) : 0)}
            decimalsLimit={2}
            maxLength={20} />
          {diferencia !== 0 && valor > 0 && (
            <small className="text-600">
              {`Tesorería registrará un ${diferencia > 0 ? 'ingreso' : 'egreso'} por ${pesos(Math.abs(diferencia))}.`}
            </small>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="motivoCorreccionPago">Motivo de la corrección</Label>
          <Input type="textarea" id="motivoCorreccionPago" maxLength={300} value={motivo}
            onChange={e => setMotivo(e.target.value)} />
        </FormGroup>
      </ModalBody>
      <ModalFooter className="no-border">
        <Col className="text-right">
          <Button onClick={toggle} className="mr-2" color="falcon-default" disabled={requesting}>Cancelar</Button>
          <Button onClick={onGuardar} color="primary" disabled={requesting}>
            {requesting ? <Spinner size="sm" /> : 'Guardar corrección'}
          </Button>
        </Col>
      </ModalFooter>
    </Modal>
  );
}

export default CorregirPagoModal;
